import React, { useState, useEffect, useCallback, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useTranslation } from "react-i18next"
import { useUser } from "../../hooks"

const roundToTen = (num) => Math.round(num / 10) * 10

export default function SexyBookmakerAllocationTool({ email }) {
  const { t } = useTranslation();
  const { getBookies } = useUser();
  const [bankAmount, setBankAmount] = useState(2000)
  const [allocations, setAllocations] = useState({})
  const [selectedBookmakers, setSelectedBookmakers] = useState([])
  const [bookies, setBookies] = useState([])
  const [loading, setLoading] = useState(true)
  const hasFetched = useRef(false)

  // Fetch bookies from database
  useEffect(() => {
    if (!email || hasFetched.current) return

    const fetchBookies = async () => {
      try {
        setLoading(true)
        hasFetched.current = true
        const fetchedBookies = await getBookies(email, false)
        
        // Transform bookies data for calculator
        const transformedBookies = fetchedBookies
          .filter(bookie => bookie.calculator) // Only include bookies with calculator config
          .map(bookie => {
            const calculator = bookie.calculator || {}
            return {
              id: bookie.id,
              name: bookie.name,
              provider: bookie.provider || t('noProvider'),
              priority: calculator.priority || 1,
              minAmount: calculator.minAmount || 100,
              maxAmount: calculator.maxAmount || 150,
              nextDepositAmount: Array.isArray(calculator.nextDepositAmount) 
                ? calculator.nextDepositAmount 
                : [calculator.minAmount || 100, calculator.maxAmount || 150],
              enabled: bookie.enabled || false
            }
          })
          .sort((a, b) => {
            // Sort by priority (higher number = higher priority), then by name
            if (a.priority !== b.priority) {
              return b.priority - a.priority
            }
            return a.name.localeCompare(b.name)
          })

        setBookies(transformedBookies)
        setSelectedBookmakers(transformedBookies.map(b => b.name))
        setLoading(false)
      } catch (error) {
        // Error fetching bookies
        setLoading(false)
      }
    }

    fetchBookies()
  }, [email]) // Only depend on email

  useEffect(() => {
    if (bookies.length > 0) {
      calculateAllocations()
    }
  }, [bankAmount, selectedBookmakers, bookies])

  const calculateAllocations = () => {
    let remainingAmount = bankAmount
    const reserveAmount = Math.ceil(bankAmount * 0.25 / 10) * 10 // 25% of bank, rounded up to nearest 10
    const newAllocations = {}
    
    // Initialize all allocations to 0
    bookies.forEach(bookie => {
      newAllocations[bookie.name] = 0
    })
    
    // Group selected bookies by provider and only allocate to the highest priority one per provider
    const selectedBookies = bookies.filter(b => selectedBookmakers.includes(b.name))
    const selectedByProvider = selectedBookies.reduce((acc, bookie) => {
      const provider = bookie.provider
      if (!acc[provider]) {
        acc[provider] = []
      }
      acc[provider].push(bookie)
      return acc
    }, {})
    
    // For each provider, pick the highest priority bookie and sort providers by priority
    const providerBookies = Object.keys(selectedByProvider)
      .map(provider => {
        const bookiesInProvider = selectedByProvider[provider]
        // Sort by priority (higher first) and pick the first one
        const sortedBookies = bookiesInProvider.sort((a, b) => {
          if (a.priority !== b.priority) {
            return b.priority - a.priority
          }
          return a.name.localeCompare(b.name)
        })
        return sortedBookies[0] // Only the highest priority bookie per provider
      })
      .sort((a, b) => {
        // Sort providers by their selected bookie's priority
        if (a.priority !== b.priority) {
          return b.priority - a.priority
        }
        return a.name.localeCompare(b.name)
      })

    providerBookies.forEach((bookmaker) => {
      if (remainingAmount <= reserveAmount) {
        return
      }

      let allocation = Math.min(bookmaker.maxAmount, remainingAmount - reserveAmount)
      allocation = Math.max(allocation, bookmaker.minAmount)
      allocation = roundToTen(allocation)

      if (allocation > remainingAmount - reserveAmount) {
        allocation = 0
      } else {
        remainingAmount -= allocation
      }

      newAllocations[bookmaker.name] = allocation
    })

    setAllocations(newAllocations)
  }

  const handleSliderChange = (e) => {
    setBankAmount(roundToTen(Number(e.target.value)))
  }

  const handleInputChange = (e) => {
    const value = roundToTen(Number(e.target.value))
    if (value >= 1000 && value <= 3500) {
      setBankAmount(value)
    }
  }

  const toggleBookmaker = (bookmakerName) => {
    setSelectedBookmakers(prev => 
      prev.includes(bookmakerName)
        ? prev.filter(name => name !== bookmakerName)
        : [...prev, bookmakerName]
    )
  }

  // Group bookies by provider
  const groupBookiesByProvider = (bookies) => {
    const grouped = bookies.reduce((acc, bookie) => {
      const provider = bookie.provider
      if (!acc[provider]) {
        acc[provider] = []
      }
      acc[provider].push(bookie)
      return acc
    }, {})
    
    // Sort bookies within each provider by priority (higher first) then by name
    Object.keys(grouped).forEach(provider => {
      grouped[provider].sort((a, b) => {
        if (a.priority !== b.priority) {
          return b.priority - a.priority
        }
        return a.name.localeCompare(b.name)
      })
    })
    
    return grouped
  }

  const totalAllocated = Object.values(allocations).reduce((sum, value) => sum + value, 0)
  const notAllocated = bankAmount - totalAllocated

  const BookieBadge = ({ bookmaker, isAllocated }) => {
    const isSelected = selectedBookmakers.includes(bookmaker.name)
    const allocation = allocations[bookmaker.name] || 0
    
    return (
      <motion.div
        key={bookmaker.name}
        className={`relative rounded-lg p-3 sm:p-2 border cursor-pointer transition-all duration-200 ${
          isAllocated 
            ? 'border-[#E8FC8B] bg-[#E8FC8B] bg-opacity-15 ring-1 ring-[#E8FC8B]' 
            : isSelected 
              ? 'border-[#E8FC8B] bg-[#E8FC8B] bg-opacity-5' 
              : 'border-gray-600 bg-[#0A0F0A] hover:border-gray-500'
        }`}
        onClick={() => toggleBookmaker(bookmaker.name)}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Mobile-first stacked layout, desktop side-by-side */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
          {/* Left side: Checkbox, Name, Priority */}
          <div className="flex items-center space-x-2 min-w-0 flex-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation()
                toggleBookmaker(bookmaker.name)
              }}
              className="form-checkbox h-3 w-3 text-[#E8FC8B] rounded border-gray-400 focus:ring-[#E8FC8B] flex-shrink-0"
            />
            <span className="text-white font-medium text-sm sm:text-xs uppercase truncate">{bookmaker.name}</span>
            <span className={`px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0 ${
              bookmaker.priority >= 3 
                ? 'bg-red-500 text-white' 
                : bookmaker.priority === 2 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-500 text-white'
            }`}>
              {bookmaker.priority}
            </span>
          </div>
          
          {/* Right side: Allocation */}
          <div className="flex items-center justify-end sm:justify-start space-x-2 flex-shrink-0">
            {isAllocated && (
              <span className="text-[#E8FC8B] text-xs">💰</span>
            )}
            <div className={`font-bold text-lg sm:text-base ${allocation === 0 ? 'text-gray-400' : 'text-[#E8FC8B]'}`}>
              {allocation.toFixed(0)}€
            </div>
          </div>
        </div>
      </motion.div>
    )
  }

  const ProviderCard = ({ provider, bookmakers }) => {
    const { t } = useTranslation();
    
    // Sort bookmakers by priority (higher first) then by name
    const sortedBookmakers = [...bookmakers].sort((a, b) => {
      if (a.priority !== b.priority) {
        return b.priority - a.priority
      }
      return a.name.localeCompare(b.name)
    })
    
    // Calculate total allocation for this provider
    const totalProviderAllocation = bookmakers.reduce((sum, bookie) => {
      return sum + (allocations[bookie.name] || 0)
    }, 0)
    
    const selectedCount = bookmakers.filter(bookie => selectedBookmakers.includes(bookie.name)).length
    
    // Get provider deposit info (same for all bookies in provider)
    const firstBookie = bookmakers[0]
    const minDeposit = firstBookie.nextDepositAmount[0] || firstBookie.minAmount
    const maxDeposit = firstBookie.nextDepositAmount[1] || firstBookie.maxAmount
    
    return (
      <motion.div
        className="rounded-xl p-4 sm:p-6 shadow-lg bg-[#111E12]"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Provider Header - Mobile-first responsive layout */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
              <h3 className="text-lg sm:text-xl font-semibold text-white uppercase truncate">{provider}</h3>
              <div className="flex items-center gap-2">
                <span className="px-2 sm:px-3 py-1 bg-[#E8FC8B] bg-opacity-20 text-[#E8FC8B] rounded-full text-xs sm:text-sm flex-shrink-0">
                  {bookmakers.length} {bookmakers.length === 1 ? t('bookie') : t('bookies')}
                </span>
                {selectedCount > 0 && (
                  <span className="px-2 py-1 bg-green-500 bg-opacity-20 text-green-400 rounded-full text-xs flex-shrink-0">
                    {selectedCount} {t('selected')}
                  </span>
                )}
              </div>
            </div>
            <div className="text-left sm:text-right flex-shrink-0">
              <div className={`font-bold text-xl sm:text-2xl ${totalProviderAllocation === 0 ? 'text-gray-400' : 'text-[#E8FC8B]'}`}>
                {totalProviderAllocation.toFixed(0)}€
              </div>
              <div className="text-xs text-gray-400">{t('total')}</div>
            </div>
          </div>
        </div>
        
        {/* Provider Actions */}
        <div className="mb-4 pb-3 border-b border-gray-700 space-y-3 sm:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <button
              onClick={() => {
                const allSelected = bookmakers.every(bookie => selectedBookmakers.includes(bookie.name))
                if (allSelected) {
                  // Deselect all bookies from this provider
                  setSelectedBookmakers(prev => prev.filter(name => !bookmakers.some(bookie => bookie.name === name)))
                } else {
                  // Select all bookies from this provider
                  const newSelections = bookmakers.map(bookie => bookie.name)
                  const uniqueSelections = [...selectedBookmakers]
                  newSelections.forEach(name => {
                    if (!uniqueSelections.includes(name)) {
                      uniqueSelections.push(name)
                    }
                  })
                  setSelectedBookmakers(uniqueSelections)
                }
              }}
              className="px-3 py-1 bg-[#E8FC8B] bg-opacity-20 text-[#E8FC8B] rounded-lg text-sm hover:bg-opacity-30 transition-all self-start"
            >
              {selectedCount === bookmakers.length ? t('deselectAll') : t('selectAll')}
            </button>
            <div className="text-xs text-gray-400 break-words">
              <div className="sm:hidden">
                {/* Mobile - stack info vertically */}
                <div>{t('min')}: {firstBookie.minAmount}€</div>
                <div>{t('nextDepositRange')}: {minDeposit}€ - {maxDeposit}€</div>
                <div>{t('priority')}: {Math.max(...bookmakers.map(b => b.priority))}</div>
              </div>
              <div className="hidden sm:block">
                {/* Desktop - inline */}
                {t('min')}: {firstBookie.minAmount}€ • {t('nextDepositRange')}: {minDeposit}€ - {maxDeposit}€ • {t('priority')}: {Math.max(...bookmakers.map(b => b.priority))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Bookmaker Badges Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 gap-2">
          <AnimatePresence>
            {sortedBookmakers.map((bookmaker) => (
              <BookieBadge 
                key={bookmaker.name} 
                bookmaker={bookmaker} 
                isAllocated={allocations[bookmaker.name] > 0}
              />
            ))}
          </AnimatePresence>
        </div>
      </motion.div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center w-full max-w-[1600px] mx-auto">
        <div className="text-white text-xl">{t('loading')}</div>
      </div>
    )
  }

  const groupedBookies = groupBookiesByProvider(bookies)
  // Sort providers by their highest priority bookie, then alphabetically
  const providerKeys = Object.keys(groupedBookies).sort((a, b) => {
    const maxPriorityA = Math.max(...groupedBookies[a].map(bookie => bookie.priority))
    const maxPriorityB = Math.max(...groupedBookies[b].map(bookie => bookie.priority))
    
    if (maxPriorityA !== maxPriorityB) {
      return maxPriorityB - maxPriorityA // Higher priority first
    }
    return a.localeCompare(b) // Alphabetical fallback
  })

  return (
    <div className="min-h-screen flex items-start justify-center w-full max-w-[1600px] mx-auto">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-[#192619] rounded-xl shadow-2xl w-full overflow-hidden"
      >
        <div className="bg-[#192619] text-white p-4 sm:p-8 relative overflow-hidden">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 20 }}
            className="absolute -top-12 -right-12 w-40 h-40 bg-[#E8FC8B] rounded-full opacity-10"
          />
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">{t("calculator")}</h1>
          <p className="text-gray-300 mt-2 text-sm sm:text-base">{t("calculatorExplanation")}</p>
        </div>
        <div className="p-4 sm:p-8 space-y-6 sm:space-y-8">
          <div className="space-y-4">
            <label htmlFor="bankAmount" className="text-white font-medium text-lg block">
              {t("totalBankAmount")}
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:space-x-4">
              <input
                type="range"
                id="bankAmountSlider"
                min="1000"
                max="3500"
                step="10"
                value={bankAmount}
                onChange={handleSliderChange}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="relative flex-shrink-0">
                <input
                  id="bankAmount"
                  type="number"
                  value={bankAmount}
                  onChange={handleInputChange}
                  min="1000"
                  max="3500"
                  className="pl-8 w-full sm:w-28 py-2 px-3 border border-gray-300 rounded-md text-[#1B2D1A] focus:outline-none focus:ring-2 focus:ring-[#E8FC8B] focus:border-[#E8FC8B]"
                />
              </div>
            </div>
          </div>
          
          {bookies.length === 0 ? (
            <div className="text-center text-gray-300 py-8">
              <p>{t('noBookies')}</p>
            </div>
                     ) : (
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
               {providerKeys.map(provider => (
                 <ProviderCard 
                   key={provider} 
                   provider={provider} 
                   bookmakers={groupedBookies[provider]} 
                 />
               ))}
             </div>
           )}
          
          <motion.div 
            className="pt-4 border-t border-[#ABCDDF] space-y-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex justify-between items-center text-white font-semibold">
              <span className="text-lg">{t("totalAllocated")}</span>
              <span className="text-[#E8FC8B] text-xl">{totalAllocated.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between items-center text-white font-semibold">
              <span className="text-lg">{t("remaining")}</span>
              <span className="text-[#B8DCEF] text-xl">{notAllocated.toFixed(2)}€</span>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  )
}
